/* assets/novatto-cart-mirror.js
   Novatto – Matching Mirror Sync (Cart + Cart Drawer)

   Purpose:
   - Keeps mirror add-on line-items in sync with their parent vanity in the cart:
       1) If parent removed -> remove mirror
       2) If quantity differs -> update mirror quantity to match parent
   - Uses strongest link first: _nov_parent_key, then fallback to _matching_mirror_for (handle).

   IMPORTANT:
   - Never /cart/add.js anything.
   - Only reads (/cart.js) and adjusts (/cart/change.js).
*/

(function () {
  'use strict';

  // ✅ Prevent cart script from interfering on product pages
  if (
    window.location.pathname.startsWith('/products/') ||
    document.querySelector('product-info') ||
    document.querySelector('form[action*="/cart/add"]')
  ) {
    return;
  }

  const NOV_LOCK_KEY = '__nov_cart_mirror_lock__';
  const LOCK_MS = 1500;

  function withCartMirrorLock(fn) {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(NOV_LOCK_KEY) || '0');
    if (now - last < LOCK_MS) return;
    sessionStorage.setItem(NOV_LOCK_KEY, String(now));
    try { fn(); } catch (e) { console.error('[Novatto cart mirror] lock fn error', e); }
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
  }

  function cartChange(line, quantity, properties) {
    const body = { line, quantity };
    if (properties) body.properties = properties;

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then((r) => r.json());
  }

  function getProp(item, name) {
    const p = item && item.properties;
    if (!p) return null;
    const v = p[name];
    return typeof v === 'string' ? v : (v != null ? String(v) : null);
  }

  // A "mirror add-on" is defined by either:
  // - _nov_bundle_role === "mirror" (new system)
  // - OR legacy _matching_mirror_for (old system)
  function findMirrorLines(cart) {
    const mirrors = [];
    (cart.items || []).forEach((item, idx) => {
      const role = (getProp(item, '_nov_bundle_role') || '').toLowerCase();
      const parentKey = getProp(item, '_nov_parent_key');
      const legacyHandle = getProp(item, '_matching_mirror_for');

      const isMirrorAddon = role === 'mirror' || (legacyHandle && legacyHandle.trim().length);

      if (!isMirrorAddon) return;

      mirrors.push({
        line: idx + 1,
        item,
        parentKey: parentKey ? parentKey.trim() : null,
        legacyHandle: legacyHandle ? legacyHandle.trim() : null
      });
    });
    return mirrors;
  }

  function buildParentMaps(cart) {
    const byKey = new Map();
    const byHandle = new Map();

    (cart.items || []).forEach((item, idx) => {
      if (item && item.key) byKey.set(item.key, { line: idx + 1, item });

      // handle fallback
      let handle = item.handle;
      if (!handle && item.url) {
        const m = String(item.url).match(/\/products\/([^/?#]+)/);
        if (m) handle = m[1];
      }
      if (handle) byHandle.set(handle, { line: idx + 1, item });
    });

    return { byKey, byHandle };
  }

  async function syncOnce() {
    try {
      const cart = await getCart();
      const mirrors = findMirrorLines(cart);
      if (!mirrors.length) return;

      const { byKey, byHandle } = buildParentMaps(cart);

      const changes = [];

      mirrors.forEach((m) => {
        let parent = null;

        // ✅ Strongest link: parent cart key
        if (m.parentKey) parent = byKey.get(m.parentKey) || null;

        // Fallback: legacy handle link
        if (!parent && m.legacyHandle) parent = byHandle.get(m.legacyHandle) || null;

        // If parent missing => remove mirror
        if (!parent) {
          changes.push({ line: m.line, quantity: 0 });
          return;
        }

        // Sync quantities
        const parentQty = Number(parent.item.quantity || 1);
        const mirrorQty = Number(m.item.quantity || 1);
        if (parentQty !== mirrorQty) {
          changes.push({ line: m.line, quantity: parentQty });
        }
      });

      if (!changes.length) return;

      // Apply sequentially; line numbers can shift, so re-fetch each time
      for (const ch of changes) {
        const fresh = await getCart();
        const freshMirrors = findMirrorLines(fresh);

        // Re-target by matching original line item "key" if possible
        const target = freshMirrors.find((x) => x.item && x.item.key && fresh.items[ch.line - 1] && fresh.items[ch.line - 1].key === x.item.key)
          || freshMirrors.find((x) => x.line === ch.line);

        if (!target) continue;
        await cartChange(target.line, ch.quantity);
      }
    } catch (err) {
      console.error('[Novatto cart mirror] syncOnce error', err);
    }
  }

  let t = null;
  function scheduleSync() {
    clearTimeout(t);
    t = setTimeout(() => withCartMirrorLock(() => void syncOnce()), 250);
  }

  document.addEventListener('DOMContentLoaded', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);
  document.addEventListener('cart:update', scheduleSync);

  function startObservers() {
    const targets = [];

    const drawer = document.querySelector('cart-drawer') || document.querySelector('#CartDrawer');
    if (drawer) targets.push(drawer);

    const cartMain =
      document.querySelector('cart-items') ||
      document.querySelector('main#MainContent') ||
      document.querySelector('#MainContent');
    if (cartMain) targets.push(cartMain);

    if (!targets.length) targets.push(document.body || document.documentElement);

    const obs = new MutationObserver(scheduleSync);
    targets.forEach((el) => {
      try { obs.observe(el, { childList: true, subtree: true }); } catch (_) {}
    });
  }

  startObservers();
})();
